// src/app/redux/sagas/cataloguesSaga.js

import { put } from "redux-saga/effects";
import Swal from "sweetalert2";

import {
    fetchCataloguesRequest,
    fetchCataloguesSuccess,
    fetchCataloguesFail,

    createCatalogueSuccess,
    createCatalogueFail,

    updateCatalogueSuccess,
    updateCatalogueFail,

    deleteCatalogueSuccess,
    deleteCatalogueFail,

    setCataloguesLoading,
} from "../actions/cataloguesActions";

import { API_BASE_URL, getRequest, postRequest, putRequest, deleteRequest } from "../../../api";
import { FETCH_FORMATIONS_REQUEST } from "../actions/formationsActions";

const getUid = () => sessionStorage.getItem("uid");
const getToken = () => sessionStorage.getItem("token");

const getSafeCataloguePayload = (catalogueId, formPayload, responseData) => {
    const fallbackPayload = {
        id: catalogueId,
        ...(formPayload || {}),
    };

    delete fallbackPayload.imageFile;
    delete fallbackPayload.file;

    if (responseData && typeof responseData === "object" && !Array.isArray(responseData)) {
        const responseCatalogue = responseData.catalogue;

        if (responseCatalogue && typeof responseCatalogue === "object" && !Array.isArray(responseCatalogue)) {
            return { ...fallbackPayload, ...responseCatalogue };
        }

        return { ...fallbackPayload, ...responseData };
    }

    return fallbackPayload;
};

const getSafeCreatedCataloguePayload = (formPayload, responseData) => {
    const fallbackPayload = {
        ...(formPayload || {}),
    };

    delete fallbackPayload.imageFile;
    delete fallbackPayload.file;

    if (responseData && typeof responseData === "object" && !Array.isArray(responseData)) {
        const responseCatalogue = responseData.catalogue;

        if (responseCatalogue && typeof responseCatalogue === "object" && !Array.isArray(responseCatalogue)) {
            return { ...fallbackPayload, ...responseCatalogue };
        }

        const hasCatalogueShape =
            responseData.id !== undefined ||
            responseData.reference !== undefined ||
            responseData.nom !== undefined;

        if (hasCatalogueShape) {
            return { ...fallbackPayload, ...responseData };
        }
    }

    return fallbackPayload;
};

export async function showCatalogueAlert(config) {
    try {
        await Swal.fire(config);
    } catch (alertError) {
        console.warn("Catalogue alert warning:", alertError);
    }
}

export function buildCatalogueFormData(payload) {
    const fd = new FormData();
    const imageFile = payload?.imageFile || null;
    const attachFile = payload?.file || null;
    const clean = { ...(payload || {}) };

    delete clean.imageFile;
    delete clean.file;

    fd.append("payload", JSON.stringify(clean));
    if (imageFile) fd.append("image", imageFile);
    if (attachFile) fd.append("file", attachFile);

    return fd;
}

export function* fetchCataloguesSaga() {
    const url = `${API_BASE_URL}/api/catalogues`;
    const uid = getUid();
    const token = getToken();

    yield put(setCataloguesLoading(true));

    try {
        const resp = yield getRequest({ url, uid, token });
        yield put(fetchCataloguesSuccess(resp?.data || []));
    } catch (e) {
        yield put(fetchCataloguesFail(e.response?.data?.message || e.message));
    } finally {
        yield put(setCataloguesLoading(false));
    }
}

export function* createCatalogueSaga(action) {
    const url = `${API_BASE_URL}/api/catalogues`;
    const uid = getUid();
    const token = getToken();
    const payload = action?.payload || {};

    yield put(setCataloguesLoading(true));

    try {
        const fd = buildCatalogueFormData(payload);
        const resp = yield postRequest({
            url,
            data: fd,
            uid,
            token,
            headers: { "Content-Type": "multipart/form-data" },
        });

        const responseData = resp?.data;
        const createdCatalogue = getSafeCreatedCataloguePayload(payload, responseData);
        const successMessage = responseData?.message || "Catalogue ajouté avec succès";

        try {
            yield put(createCatalogueSuccess(createdCatalogue));
        } catch (dispatchError) {
            console.error("createCatalogueSuccess dispatch error:", dispatchError);
        }

        try {
            yield put(fetchCataloguesRequest());
        } catch (refreshError) {
            console.error("fetchCataloguesRequest dispatch error:", refreshError);
        }

        try {
            yield put({ type: FETCH_FORMATIONS_REQUEST });
        } catch (formationsRefreshError) {
            console.error("FETCH_FORMATIONS_REQUEST dispatch error:", formationsRefreshError);
        }

        yield showCatalogueAlert({
            icon: "success",
            text: successMessage,
            timer: 1400,
            showConfirmButton: false,
        });
    } catch (e) {
        const msg = e.response?.data?.message || "Erreur ajout catalogue";

        try {
            yield put(createCatalogueFail(msg));
        } catch (dispatchError) {
            console.error("createCatalogueFail dispatch error:", dispatchError);
        }

        yield showCatalogueAlert({
            icon: "error",
            text: msg,
            timer: 2200,
            showConfirmButton: false,
        });
    } finally {
        try {
            yield put(setCataloguesLoading(false));
        } catch (loadingError) {
            console.error("setCataloguesLoading(false) error:", loadingError);
        }
    }
}

export function* updateCatalogueSaga(action) {
    const { id, payload } = action.payload || {};
    const url = `${API_BASE_URL}/api/catalogues/${id}`;
    const uid = getUid();
    const token = getToken();

    yield put(setCataloguesLoading(true));

    try {
        const fd = buildCatalogueFormData(payload);
        const resp = yield putRequest({
            url,
            data: fd,
            uid,
            token,
            headers: { "Content-Type": "multipart/form-data" },
        });

        const responseData = resp?.data;
        const updatedCatalogue = getSafeCataloguePayload(id, payload, responseData);
        const successMessage = responseData?.message || "Catalogue modifi\u00E9 avec succ\u00E8s";

        try {
            yield put(updateCatalogueSuccess(updatedCatalogue));
        } catch (dispatchError) {
            console.error("updateCatalogueSuccess dispatch error:", dispatchError);
        }

        try {
            yield put(fetchCataloguesRequest());
        } catch (refreshError) {
            console.error("fetchCataloguesRequest dispatch error:", refreshError);
        }

        yield showCatalogueAlert({
            icon: "success",
            text: successMessage,
            timer: 1400,
            showConfirmButton: false,
        });
    } catch (e) {
        const msg = e.response?.data?.message || "Erreur modification catalogue";

        try {
            yield put(updateCatalogueFail(msg));
        } catch (dispatchError) {
            console.error("updateCatalogueFail dispatch error:", dispatchError);
        }

        yield showCatalogueAlert({
            icon: "error",
            text: msg,
            timer: 2200,
            showConfirmButton: false,
        });
    } finally {
        try {
            yield put(setCataloguesLoading(false));
        } catch (loadingError) {
            console.error("setCataloguesLoading(false) error:", loadingError);
        }
    }
}

export function* deleteCatalogueSaga(action) {
    const { id } = action.payload;
    const url = `${API_BASE_URL}/api/catalogues/${id}`;
    const uid = getUid();
    const token = getToken();

    yield put(setCataloguesLoading(true));

    try {
        yield deleteRequest({ url, uid, token });
        yield put(deleteCatalogueSuccess(id));
        yield showCatalogueAlert({ icon: "success", text: "Catalogue supprime", timer: 1400, showConfirmButton: false });
    } catch (e) {
        const msg = e.response?.data?.message || "Erreur suppression catalogue";
        yield put(deleteCatalogueFail(msg));
        yield showCatalogueAlert({ icon: "error", text: msg, timer: 2200, showConfirmButton: false });
    } finally {
        yield put(setCataloguesLoading(false));
    }
}
